import {
	type CalendarDate,
	EpochTimeStampUtils,
} from "@pilatestime/shared-types";
import { useTranslation } from "react-i18next";

/**
 * Custom hook for formatting dates according to the selected language/locale
 */
export function useDateFormatter() {
	const { i18n } = useTranslation();

	// Convert i18n language format (en_US, pt_BR) to locale format (en-US, pt-BR)
	const locale = i18n.language.replace("_", "-");

	/**
	 * Format an epoch timestamp to a localized date string
	 * @param epochTime - Epoch timestamp in milliseconds
	 * @returns Formatted date string according to current locale
	 */
	const formatDate = (epochTime: number): string => {
		return EpochTimeStampUtils.toDisplayFormat(epochTime, locale);
	};

	/**
	 * Format an epoch timestamp to a localized date string with custom options
	 * @param epochTime - Epoch timestamp in milliseconds
	 * @param options - Intl.DateTimeFormatOptions for custom formatting
	 * @returns Formatted date string according to current locale
	 */
	const formatDateWithOptions = (
		epochTime: number,
		options: Intl.DateTimeFormatOptions,
	): string => {
		const date = new Date(epochTime);
		return date.toLocaleDateString(locale, options);
	};

	/**
	 * Format an epoch timestamp to a localized date and time string
	 * @param epochTime - Epoch timestamp in milliseconds
	 * @param options - Intl.DateTimeFormatOptions for custom formatting
	 * @returns Formatted date and time string according to current locale
	 */
	const formatDateTime = (
		epochTime: number,
		options?: Intl.DateTimeFormatOptions,
	): string => {
		const date = new Date(epochTime);
		const defaultOptions: Intl.DateTimeFormatOptions = {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			...options,
		};
		return date.toLocaleDateString(locale, defaultOptions);
	};

	/**
	 * Format an epoch timestamp to a localized time string
	 * @param epochTime - Epoch timestamp in milliseconds
	 * @param options - Intl.DateTimeFormatOptions for custom formatting
	 * @returns Formatted time string according to current locale
	 */
	const formatTime = (
		epochTime: number,
		options?: Intl.DateTimeFormatOptions,
	): string => {
		const date = new Date(epochTime);
		const defaultOptions: Intl.DateTimeFormatOptions = {
			hour: "2-digit",
			minute: "2-digit",
			...options,
		};
		return date.toLocaleTimeString(locale, defaultOptions);
	};

	/**
	 * Format an epoch timestamp for date input fields (YYYY-MM-DD)
	 * This format is required for HTML date inputs and is locale-independent
	 * @param epochTime - Epoch timestamp in milliseconds
	 * @returns Date string in YYYY-MM-DD format
	 */
	const formatForDateInput = (epochTime: number): string => {
		return EpochTimeStampUtils.toDateInputFormat(epochTime);
	};

	/**
	 * Format a {@link CalendarDate} — a day with no time and no zone, such as a
	 * date of birth, a session day, or a billing period bound. The day is
	 * rendered in UTC so the exact "YYYY-MM-DD" that was stored is what the user
	 * reads, in every zone.
	 *
	 * The parameter accepts *only* `CalendarDate`, never a `number` or `Date`.
	 * That is the mechanism — not a convention — that keeps {@link formatDate}
	 * (the instant formatter) from being reached for a calendar day: rendering a
	 * calendar day through `formatDate` shows the previous day for every viewer
	 * west of Greenwich, including all of Brazil, because UTC midnight is still
	 * the evening before in local time.
	 *
	 * `timeZone` is pinned and cannot be overridden through `options`: reading
	 * these values in any other zone is the bug this function exists to prevent.
	 *
	 * @param date - The calendar day as a `CalendarDate` ("YYYY-MM-DD")
	 * @param options - Field widths to override, e.g. `{ day: "numeric", month: "short" }`
	 * @returns Localized date string (e.g. "10/17/2025" for en-US, "17/10/2025" for pt-BR)
	 */
	const formatCalendarDate = (
		date: CalendarDate,
		options?: Omit<Intl.DateTimeFormatOptions, "timeZone">,
	): string => {
		const year = Number(date.slice(0, 4));
		const month = Number(date.slice(5, 7));
		const day = Number(date.slice(8, 10));
		const utcMidnight = new Date(Date.UTC(year, month - 1, day));
		return utcMidnight.toLocaleDateString(locale, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			...options,
			timeZone: "UTC",
		});
	};

	/**
	 * Format an epoch timestamp to a relative time string (e.g., "2 days ago", "in 3 hours")
	 * @param epochTime - Epoch timestamp in milliseconds
	 * @returns Relative time string according to current locale
	 */
	const formatRelativeTime = (epochTime: number): string => {
		const now = Date.now();
		const diffInSeconds = Math.floor((epochTime - now) / 1000);

		// Use Intl.RelativeTimeFormat for proper localization
		const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

		const absDiff = Math.abs(diffInSeconds);

		if (absDiff < 60) {
			return rtf.format(diffInSeconds, "second");
		}
		if (absDiff < 3600) {
			return rtf.format(Math.floor(diffInSeconds / 60), "minute");
		}
		if (absDiff < 86400) {
			return rtf.format(Math.floor(diffInSeconds / 3600), "hour");
		}
		if (absDiff < 2592000) {
			return rtf.format(Math.floor(diffInSeconds / 86400), "day");
		}
		if (absDiff < 31536000) {
			return rtf.format(Math.floor(diffInSeconds / 2592000), "month");
		}
		return rtf.format(Math.floor(diffInSeconds / 31536000), "year");
	};

	return {
		formatDate,
		formatDateWithOptions,
		formatDateTime,
		formatTime,
		formatForDateInput,
		formatCalendarDate,
		formatRelativeTime,
		locale, // Expose locale in case components need it
	};
}
